import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, RotateCcw, Scale } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, get, errMsg } from "@/lib/api";
import { parseChip, SOURCE_LABELS } from "@/lib/candidates";
import { stageLabel } from "@/lib/charts";
import { Skel } from "@/components/app/Common";
import { StatusChip } from "@/components/app/StatusChip";
import { FitDial } from "@/components/fit/FitDial";
import { RecChip } from "@/components/fit/FitBits";
import { LeaningChip, WaitingOn } from "@/components/fit/Provisional";
import { HeaderActions } from "@/pages/candidates/overview/HeaderActions";
import { ConsentBadge } from "@/components/intake/ConsentBadge";

const Chip = ({ icon: Icon, children, testId }) => <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2.5 py-0.5 text-xs text-ink-secondary ring-1 ring-line" data-testid={testId}>{Icon && <Icon size={12} aria-hidden="true" />}{children}</span>;

export function HeaderShell({ id, name }) {
  return (
    <div className="tl-hero sticky top-[72px] z-20 flex items-center gap-5 p-5" data-testid="profile-header-shell">
      <Skel className="h-[84px] w-[84px] rounded-full" />
      <div className="flex-1 space-y-2">
        {name ? <motion.h1 layoutId={`cand-name-${id}`} className="w-fit text-2xl font-semibold tracking-tight">{name}</motion.h1> : <Skel className="h-7 w-56" />}
        <Skel className="h-4 w-72" />
      </div>
    </div>
  );
}

export function ProfileHeader({ c, app, canEdit, refresh, onDecide }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["app-fit", app?.id], queryFn: () => get(`/applications/${app.id}/fit`), enabled: !!app?.screening });
  const fit = data?.fit;
  const reparse = async () => { try { await api.post(`/candidates/${c.id}/reparse`); toast.success("Parsing queued again"); refresh(); qc.invalidateQueries({ queryKey: ["candidate", c.id] }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="tl-hero sticky top-[72px] z-20 flex flex-wrap items-center gap-x-6 gap-y-4 p-4 sm:p-5" data-tour="pt-profile-sticky-header" data-testid="profile-sticky-header">
      {fit && <FitDial score={fit.fit_score} provisional={fit.provisional} size={84} testId="header-fit-dial" label="Fit" />}
      <div className="min-w-[220px] flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted"><Link to="/app/candidates" className="hover:text-ink">Candidates</Link>{app && <><span>/</span><span>{app.job_title}</span></>}</div>
        <motion.h1 layoutId={`cand-name-${c.id}`} className="w-fit text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="candidate-name">{c.display_name}</motion.h1>
        <div className="flex flex-wrap items-center gap-2">
          {fit && (fit.provisional ? <LeaningChip rec={fit.recommendation} testId="header-recommendation" /> : <RecChip rec={fit.recommendation} testId="header-recommendation" />)}
          {app && <Chip testId="header-stage">{stageLabel(app.stage)}</Chip>}
          <StatusChip status={parseChip(c)} testId="candidate-parse-status" />
          {c.email && <Chip icon={Mail} testId="candidate-email">{c.email}</Chip>}
          {c.phone && <Chip icon={Phone} testId="candidate-phone">{c.phone}</Chip>}
          {c.location && <Chip icon={MapPin}>{c.location}</Chip>}
          {c.parsed?.pqe_years != null && <Chip icon={Scale} testId="candidate-pqe">{c.parsed.pqe_years} yrs PQE</Chip>}
          {c.parsed?.bar_council && <Chip testId="candidate-bar-council">{c.parsed.bar_council}</Chip>}
          {c.parsed?.enrolment_number && <Chip testId="candidate-enrolment-no">Enrolment no. {c.parsed.enrolment_number}</Chip>}
          <Chip testId="candidate-source">Source: {SOURCE_LABELS[c.source] || c.source}</Chip>
          <ConsentBadge consent={c.consent} testId="profile-consent-pending" />
        </div>
        {fit?.provisional && <WaitingOn missing={fit.missing} testId="header-waiting-on" />}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {canEdit && c.resume_file_id && <Button variant="ghost" size="sm" onClick={reparse} data-testid="candidate-reparse-btn"><RotateCcw size={14} /> Re-read resume</Button>}
        {app?.screening && <HeaderActions app={app} c={c} canEdit={canEdit} onDecide={onDecide} />}
      </div>
    </div>
  );
}
